import { type CSSProperties, useEffect, useRef, type ReactNode } from "react";
import { clientLog } from "./clientLog";

export type SkeletonShape = "list" | "table" | "cards" | "form" | "detail" | "chart" | "map" | "treemap";

/** Same as the fade-in delay of `.skeleton` in styles.css. A quicker load shows no placeholder. */
export const SKELETON_DELAY_MS = 150;

const LIST_ROWS = 5;
const TABLE_ROWS = 6;
const TABLE_COLUMNS = 5;
const CARD_COUNT = 6;
const FORM_ROWS = 4;
const CHART_BARS = [45, 70, 55, 85, 60, 40, 75, 50, 90, 65, 35, 55];
const WIDTHS = [
  [62, 38, 24],
  [48, 30, 18],
  [70, 44, 28],
  [54, 26, 20],
  [66, 36, 22],
];
const CELL_WIDTHS = [80, 55, 70, 40, 65, 50];

const times = (count: number) => Array.from({ length: count }, (_, n) => n);

/** Log `skeleton_seen` at the end of a placeholder that a person could see. */
export function trackSeen(shape: string, visible: () => boolean, now: () => number = () => performance.now()): () => void {
  const start = now();
  let seen = false;
  const timer = setTimeout(() => {
    seen = visible();
  }, SKELETON_DELAY_MS);
  return () => {
    clearTimeout(timer);
    if (seen) clientLog.event("skeleton_seen", { shape, ms: Math.round(now() - start) });
  };
}

/** A closed `<details>` keeps a box for its content, so `getClientRects` cannot tell. */
export function isShown(box: Pick<Element, "checkVisibility"> | null): boolean {
  return box?.checkVisibility?.() ?? false;
}

/** A long page keeps a placeholder under the fold: nobody sees it. */
export function onScreen(box: Pick<Element, "getBoundingClientRect"> | null): boolean {
  const rect = box?.getBoundingClientRect();
  return rect !== undefined && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
}

/** One grey rounded bar. It says nothing to a screen reader. */
export function SkeletonBar({ width, height, className }: { width?: string; height?: string; className?: string }) {
  return <span className={className ? `skeleton-bar ${className}` : "skeleton-bar"} style={{ width, height }} aria-hidden="true" />;
}

/** The status box of a placeholder: use it direct only for a page with its own layout. */
export function SkeletonFrame({ shape, label = "Loading", overlay, children }: { shape: string; label?: string; overlay?: boolean; children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => trackSeen(shape, () => isShown(box.current) && onScreen(box.current)), [shape]);
  return (
    <div ref={box} className={overlay ? `skeleton skeleton-${shape} skeleton-overlay` : `skeleton skeleton-${shape}`} role="status" data-testid={overlay ? "skeleton-map" : "skeleton"} data-shape={shape}>
      <span className="visually-hidden">{label}</span>
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

function ListRows({ rows }: { rows: number }) {
  return times(rows).map((n) => (
    <div key={n} className="skeleton-row">
      {WIDTHS[n % WIDTHS.length].map((width) => (
        <SkeletonBar key={width} width={`${width}%`} />
      ))}
    </div>
  ));
}

function TableRows({ rows, columns }: { rows: number; columns: number }) {
  const cells = (row: number) =>
    times(columns).map((n) => (
      <div key={n} className="skeleton-cell" style={{ "--cell-bar": `${CELL_WIDTHS[(n + row) % CELL_WIDTHS.length]}%` } as CSSProperties}>
        <SkeletonBar />
      </div>
    ));
  return (
    <>
      <div className="skeleton-table-row skeleton-table-head">{cells(0)}</div>
      {times(rows).map((n) => (
        <div key={n} className="skeleton-table-row">
          {cells(n + 1)}
        </div>
      ))}
    </>
  );
}

function Cards({ count }: { count: number }) {
  return (
    <div className="skeleton-card-grid">
      {times(count).map((n) => (
        <div key={n} className="skeleton-card">
          <SkeletonBar width="50%" />
          <SkeletonBar width="35%" className="skeleton-big" />
          <SkeletonBar width="75%" />
        </div>
      ))}
    </div>
  );
}

function FormRows({ rows, phoneRows = rows }: { rows: number; phoneRows?: number }) {
  return times(Math.max(rows, phoneRows)).map((n) => (
    <div key={n} className={n < rows ? "skeleton-field" : "skeleton-field skeleton-phone"}>
      <SkeletonBar width={`${WIDTHS[n % WIDTHS.length][2]}%`} />
      <SkeletonBar className="skeleton-input" />
    </div>
  ));
}

function Detail({ rows }: { rows: number }) {
  return (
    <>
      <SkeletonBar width="45%" className="skeleton-title" />
      <SkeletonBar width="28%" />
      <div className="skeleton-tabs">
        {times(4).map((n) => (
          <SkeletonBar key={n} width="4.5rem" />
        ))}
      </div>
      <ListRows rows={rows} />
    </>
  );
}

function Chart() {
  return (
    <div className="skeleton-plot">
      {CHART_BARS.map((height, n) => (
        <SkeletonBar key={n} width="100%" className="skeleton-column" height={`${height}%`} />
      ))}
    </div>
  );
}

/** The grey shape of the content that loads. `rows` counts cards for `cards`; `overlay` covers a map box of the page. */
export function Skeleton({ shape = "list", rows, phoneRows, columns, label, overlay }: { shape?: SkeletonShape; rows?: number; phoneRows?: number; columns?: number; label?: string; overlay?: boolean }) {
  return (
    <SkeletonFrame shape={shape} label={label} overlay={overlay}>
      {shape === "list" && <ListRows rows={rows ?? LIST_ROWS} />}
      {shape === "table" && <TableRows rows={rows ?? TABLE_ROWS} columns={columns ?? TABLE_COLUMNS} />}
      {shape === "cards" && <Cards count={rows ?? CARD_COUNT} />}
      {shape === "form" && <FormRows rows={rows ?? FORM_ROWS} phoneRows={phoneRows} />}
      {shape === "detail" && <Detail rows={rows ?? LIST_ROWS - 1} />}
      {shape === "chart" && <Chart />}
      {shape === "treemap" && <div className="skeleton-fill skeleton-treemap" />}
    </SkeletonFrame>
  );
}

/** The line of the list tools over a table, which comes with the rows. */
export function SkeletonTools() {
  return (
    <div className="list-tools skeleton skeleton-tools" aria-hidden="true">
      <SkeletonBar width="6rem" />
    </div>
  );
}

/** A settings section while it loads: the same fieldset as the section, with about the rows of the section. */
export function SkeletonSection({ title, rows, phoneRows }: { title: string; rows: number; phoneRows?: number }) {
  return (
    <fieldset aria-busy="true">
      <legend>{title}</legend>
      <Skeleton shape="form" rows={rows} phoneRows={phoneRows} />
    </fieldset>
  );
}
